import { useState, useMemo } from 'react';
import { Plus, FileText } from 'lucide-react';
import { Card, CardContent, ErrorState, useToast } from '@mustauto/ui/web';
import { EmptyState } from '@mustauto/ui/web';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@mustauto/ui/web';
import { useAuth } from '@/hooks/useAuth';
import { usePurchaseOrders, usePurchaseOrder } from '@/hooks/usePurchaseOrders';
import type { POStatus, PurchaseOrder } from '@/hooks/usePurchaseOrders';
import { formatReais } from '@/lib/utils';
import { POStatusBadge, POAlertBadge } from './StatusBadge';
import type { POAlert } from './StatusBadge';
import { PurchaseOrderForm } from './PurchaseOrderForm';
import { PurchaseOrderModal } from './PurchaseOrderModal';
import { ReceiveModal } from './ReceiveModal';
import { addDaysIsoDate, formatApiError, formatDateBR, todayIsoIn } from '@mustauto/utils';
import { useCan } from '@/hooks/useCan';

const ACTIVE_STATUSES: POStatus[] = ['pending', 'approved', 'ordered', 'partial'];

function getPOAlert(order: PurchaseOrder): POAlert {
  if (!ACTIVE_STATUSES.includes(order.status)) return null;

  const now = Date.now();

  // Atraso e proximidade comparam DIA com DIA. `expectedDelivery` é uma data
  // pura, e transformá-la em instante (meia-noite UTC) colocava a entrega de
  // hoje três horas no passado em UTC-3: uma OC combinada para hoje já nascia
  // "atrasada" na lista.
  if (order.expectedDelivery) {
    const today = todayIsoIn();
    const delivery = order.expectedDelivery.slice(0, 10);
    if (delivery < today) return 'overdue';
    if (delivery <= addDaysIsoDate(today, 3)) return 'approaching';
  }

  // Stale: no update in 7+ days, only for ordered/partial
  if ((order.status === 'ordered' || order.status === 'partial') && order.updatedAt) {
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    if (now - new Date(order.updatedAt).getTime() > sevenDays) return 'stale';
  }

  return null;
}

const STATUS_TABS: { key: POStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'draft', label: 'Rascunhos' },
  { key: 'pending', label: 'Ag. Aprovação' },
  { key: 'approved', label: 'Aprovadas' },
  { key: 'ordered', label: 'Em aberto' },
  { key: 'partial', label: 'Parciais' },
  { key: 'received', label: 'Recebidas' },
  { key: 'cancelled', label: 'Canceladas' },
];

interface PurchaseOrdersTabProps {
  preselectedItemId?: string | null;
  onClearPreselect?: () => void;
  onNavigateToSuppliers?: () => void;
}

export function PurchaseOrdersTab({ preselectedItemId, onClearPreselect, onNavigateToSuppliers }: PurchaseOrdersTabProps) {
  const { toast } = useToast();
  const { isDemo } = useAuth();
  const can = useCan();
  const [statusTab, setStatusTab] = useState<POStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(!!preselectedItemId);
  const [editOrder, setEditOrder] = useState<PurchaseOrder | null>(null);
  const [viewOrderId, setViewOrderId] = useState<string | null>(null);
  const [receiveOrder, setReceiveOrder] = useState<PurchaseOrder | null>(null);

  const statusFilter = statusTab === 'all' ? undefined : statusTab;
  const { orders, total, totalPages, isLoading, isError, error, refetch, create, update, submit, approve, reject, confirmOrder, receive,
    isCreating, isUpdating, isSubmitting, isApproving, isRejecting, isConfirming, isReceiving } =
    usePurchaseOrders({ status: statusFilter, page, limit: 20 });

  const viewOrderQuery = usePurchaseOrder(viewOrderId);
  const viewOrder = viewOrderQuery.data?.order ?? null;

  // Count pending for badge
  const { orders: pendingOrders } = usePurchaseOrders({ status: 'pending', limit: 1 });

  const statusCounts = useMemo(() => {
    const counts: Partial<Record<POStatus, number>> = {};
    if (pendingOrders.length > 0) {
      // We only know the total from the pending query, approximate
    }
    return counts;
  }, [pendingOrders]);

  return (
    <div className="space-y-4">
      {/* Status tabs + Create button */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-1 overflow-x-auto">
              {STATUS_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setStatusTab(key); setPage(1); }}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    statusTab === key
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {!isDemo && can('compras.pedir') && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors shrink-0 ml-4"
              >
                <Plus size={16} />
                Nova OC
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Orders table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : isError && orders.length === 0 ? (
        <ErrorState title="Não foi possível carregar as ordens de compra" error={error} onRetry={() => refetch()} />
      ) : orders.length === 0 ? (
        <EmptyState
          icon={<FileText size={32} />}
          title={statusTab === 'all' ? 'Nenhuma ordem de compra' : 'Nenhuma OC neste status'}
          description={
            statusTab === 'all'
              ? 'Crie ordens de compra pra registrar pedidos a fornecedores. Cada OC vira recebimento e abastece o estoque.'
              : 'Tente outra aba ou crie uma nova OC.'
          }
          action={{
            label: 'Criar primeira OC',
            onClick: () => setShowForm(true),
          }}
        />
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-right">Itens</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead>Prev. Entrega</TableHead>
                  <TableHead>Criação</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-mono text-sm font-medium">{order.number}</TableCell>
                    <TableCell className="font-medium">{order.supplierName ?? '—'}</TableCell>
                    <TableCell className="text-right font-mono">{order.itemsCount}</TableCell>
                    <TableCell className="text-right">{order.totalAmount == null ? '—' : formatReais(order.totalAmount)}</TableCell>
                    <TableCell className="text-center">
                      <POStatusBadge status={order.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {order.expectedDelivery ? (
                        <div className="flex flex-col gap-0.5">
                          <span>{formatDateBR(order.expectedDelivery)}</span>
                          {(() => {
                            const alert = getPOAlert(order);
                            return alert ? <POAlertBadge alert={alert} /> : null;
                          })()}
                        </div>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewOrderId(order.id)}
                          className="rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/40 transition-colors"
                        >
                          Ver
                        </button>
                        {order.status === 'draft' && can('compras.pedir') && (
                          <button
                            onClick={async () => {
                              try {
                                await submit(order.id);
                                toast({ type: 'success', title: 'OC enviada para aprovação' });
                              } catch (err) {
                                toast({ type: 'error', title: 'Falha ao enviar OC', description: formatApiError(err, 'Não foi possível enviar a OC. Tente novamente.') });
                              }
                            }}
                            disabled={isSubmitting}
                            className="rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40 transition-colors disabled:opacity-50"
                          >
                            Enviar
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-muted-foreground">
                Página {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Form */}
      {(showForm || editOrder) && (
        <PurchaseOrderForm
          order={editOrder}
          preselectedItemId={preselectedItemId}
          onNavigateToSuppliers={() => {
            setShowForm(false);
            setEditOrder(null);
            onClearPreselect?.();
            onNavigateToSuppliers?.();
          }}
          onSubmit={async (data) => {
            const editing = !!editOrder;
            try {
              if (editOrder) {
                await update({ id: editOrder.id, ...data });
                setEditOrder(null);
              } else {
                await create(data);
                setShowForm(false);
                onClearPreselect?.();
              }
              toast({ type: 'success', title: editing ? 'OC atualizada' : 'OC criada' });
            } catch (err) {
              toast({
                type: 'error',
                title: editing ? 'Falha ao atualizar OC' : 'Falha ao criar OC',
                description: formatApiError(err, 'Não foi possível salvar a OC. Tente novamente.'),
              });
            }
          }}
          onClose={() => {
            setShowForm(false);
            setEditOrder(null);
            onClearPreselect?.();
          }}
          isSubmitting={isCreating || isUpdating}
        />
      )}

      {/* View Order Modal */}
      {viewOrder && (
        <PurchaseOrderModal
          order={viewOrder}
          onClose={() => setViewOrderId(null)}
          onApprove={async (id) => {
            try {
              await approve(id);
              toast({ type: 'success', title: 'OC aprovada' });
            } catch (err) {
              toast({ type: 'error', title: 'Falha ao aprovar OC', description: formatApiError(err, 'Não foi possível aprovar a OC. Tente novamente.') });
            }
          }}
          onReject={async (id, reason) => {
            try {
              await reject({ id, reason });
              setViewOrderId(null);
              toast({ type: 'success', title: 'OC recusada' });
            } catch (err) {
              toast({ type: 'error', title: 'Falha ao recusar OC', description: formatApiError(err, 'Não foi possível recusar a OC. Tente novamente.') });
            }
          }}
          onConfirmOrder={async (id) => {
            try {
              await confirmOrder(id);
              toast({ type: 'success', title: 'Pedido confirmado ao fornecedor' });
            } catch (err) {
              toast({ type: 'error', title: 'Falha ao confirmar pedido', description: formatApiError(err, 'Não foi possível confirmar o pedido. Tente novamente.') });
            }
          }}
          onReceive={(order) => {
            setViewOrderId(null);
            setReceiveOrder(order);
          }}
          onEdit={(order) => {
            setViewOrderId(null);
            setEditOrder(order);
          }}
          isApproving={isApproving}
          isConfirming={isConfirming}
        />
      )}

      {/* Receive Modal */}
      {receiveOrder && (
        <ReceiveModal
          order={receiveOrder}
          onSubmit={async (data) => {
            try {
              await receive(data);
              toast({ type: 'success', title: 'Recebimento registrado' });
            } catch (err) {
              toast({ type: 'error', title: 'Falha ao registrar recebimento', description: formatApiError(err, 'Não foi possível registrar o recebimento. Tente novamente.') });
              throw err;
            }
          }}
          onClose={() => setReceiveOrder(null)}
          isSubmitting={isReceiving}
        />
      )}
    </div>
  );
}
